/**
 * Comments page — S011 shell.
 *
 * Figma authority: R4VYIdXEv7HYIXtpDMEvHY, node 38:2510
 *
 * Route: /feed/:postId/comments
 * Auth required: yes (uses auth store)
 *
 * Wires the Elm-ish comments program to the router.
 * Handles: program lifecycle, navigation on back, deterministic reference time.
 *
 * CORRECTION02:
 * - Passes FIXTURE_REFERENCE_TIME into the view so formatRelativeTime has a
 *   stable, fixture-canonical reference. Production code will replace this
 *   with a clock service.
 */
import { useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCommentsProgram } from '@/features/comments/useCommentsProgram';
import { CommentsView } from '@/features/comments/view';
import {
  createFixtureCommentsAdapter,
  FIXTURE_REFERENCE_TIME,
} from '@/features/comments/adapters/fixtureCommentsPort';

/**
 * Comments page component.
 */
export function CommentsPage(): JSX.Element {
  const navigate = useNavigate();
  const params = useParams<{ postId: string }>();
  const postId = params.postId ?? '';

  // Stable dependency identity — created once per page mount, not on every render.
  // Each CommentsPage gets its own private fixture state (closure isolation, S1).
  const dependencies = useMemo(() => createFixtureCommentsAdapter(), []);

  // Monotonic load ID counter — increments on every route change.
  const loadIdRef = useRef(0);
  const lastPostIdRef = useRef<string | null>(null);

  const nextLoadId = useCallback(() => {
    loadIdRef.current += 1;
    return loadIdRef.current;
  }, []);

  const { model, start, changeDraft, send, like } = useCommentsProgram({
    dependencies,
  });

  // Start loading when postId changes
  useEffect(() => {
    if (postId && postId !== lastPostIdRef.current) {
      lastPostIdRef.current = postId;
      start(postId, nextLoadId());
    }
  }, [postId, start, nextLoadId]);

  // Handle back navigation
  const handleBack = () => {
    navigate(-1);
  };

  return (
    <CommentsView
      model={model}
      referenceTime={FIXTURE_REFERENCE_TIME}
      onBack={handleBack}
      onDraftChange={changeDraft}
      onSend={send}
      onLike={like}
    />
  );
}